import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const sections = [
  {
    title: "About ClickNido",
    body: [
      "ClickNido is an online platform that connects customers with independent, verified service professionals for home, technical, caretaker, outdoor, transport, and special services.",
      "ClickNido facilitates the booking and payment for these services; the service itself is performed by the assigned partner, not by ClickNido directly.",
    ],
  },
  {
    title: "Booking & Payments",
    body: [
      "Prices shown at checkout reflect the final, applicable amount for your service, city, and selected options at the time of booking.",
      "You can pay upfront online, or choose \"Pay After Work\" where available.",
      "Platform fees and applicable taxes (GST) are added to the service amount and shown clearly before you confirm a booking.",
    ],
  },
  {
    title: "Cancellations & Refunds",
    body: [],
    links: [
      { label: "Cancellation Policy", path: "/cancellation-policy" },
      { label: "Refund Policy", path: "/refund-policy" },
    ],
    linksIntro: "Cancelling a booking may attract a charge depending on how far along it is. See our:",
  },
  {
    title: "User Responsibilities",
    body: [
      "You agree to provide accurate booking, address, and contact information so partners can complete your service.",
      "You're responsible for ensuring safe access to the service location and for the safety of the partner while on-site.",
      "Misuse of the platform, abusive behaviour toward partners, or fraudulent bookings may result in account suspension.",
    ],
  },
  {
    title: "Partner Conduct & Verification",
    body: [
      "Service partners undergo a verification process before being listed on the platform.",
      "If a partner's conduct falls short of our standards, please report it through Contact Us so we can investigate.",
    ],
  },
  {
    title: "Liability",
    body: [
      "ClickNido makes reasonable efforts to verify partners and ensure service quality, but does not guarantee uninterrupted or error-free service.",
      "ClickNido's liability for any claim relating to a booking is limited to the amount paid for that specific booking.",
    ],
  },
  {
    title: "Account Suspension & Termination",
    body: [
      "We may suspend or terminate accounts that violate these terms, misuse the platform, or engage in fraudulent activity.",
      "You may stop using ClickNido and request account deletion at any time.",
    ],
  },
  {
    title: "Governing Law",
    body: [
      "These terms are governed by the laws of India, and any disputes will be subject to the jurisdiction of the courts in Bangalore, Karnataka.",
    ],
  },
  {
    title: "Changes to These Terms",
    body: [
      "We may update these terms from time to time. Continued use of ClickNido after changes are posted means you accept the updated terms.",
    ],
  },
  {
    title: "Related Legal Documents",
    body: [],
    linksIntro: "These terms are part of a broader set of policies covering the platform:",
    links: [
      { label: "Company Policy", path: "/company-policy" },
      { label: "Investor Agreement Policy", path: "/investor-agreement-policy" },
      { label: "Rules & Regulations", path: "/rules-regulations" },
      { label: "Service Partner Terms & Conditions", path: "/service-partner-terms" },
      { label: "Cancellation Policy", path: "/cancellation-policy" },
      { label: "Grievance Redressal Policy", path: "/grievance-redressal-policy" },
    ],
  },
];

const TermsConditions = () => {
  const navigate = useNavigate();

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={styles.breadcrumb}>Home &nbsp;›&nbsp; Terms & Conditions</div>

        <h1 style={styles.title}>Terms & Conditions</h1>
        <p style={styles.intro}>
          By creating an account or booking a service on ClickNido, you agree to the
          following terms. Please read them carefully.
        </p>
        <p style={styles.updated}>Last updated: August 2026</p>

        {sections.map((sec) => (
          <div key={sec.title} style={styles.card}>
            <h2 style={styles.cardTitle}>{sec.title}</h2>
            {sec.body.length > 0 && (
              <ul style={styles.list}>
                {sec.body.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            )}
            {sec.links && (
              <>
                <p style={styles.contactText}>{sec.linksIntro}</p>
                <div style={styles.linkRow}>
                  {sec.links.map((lnk) => (
                    <button
                      key={lnk.path}
                      style={styles.inlineLink}
                      onClick={() => navigate(lnk.path)}
                    >
                      {lnk.label} <ArrowRight size={14} />
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        ))}

        <div style={styles.card}>
          <h2 style={styles.cardTitle}>Contact Us</h2>
          <p style={styles.contactText}>
            Questions about these terms? Reach out to us anytime.
          </p>
          <button style={styles.inlineLink} onClick={() => navigate("/contact-us")}>
            Contact Us <ArrowRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  page: {
    background: "#f5f6fb",
    minHeight: "100vh",
    padding: "40px 16px",
    fontFamily:
      "Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    color: "#111827",
  },
  container: {
    maxWidth: "860px",
    margin: "0 auto",
  },
  breadcrumb: {
    color: "#7b8198",
    fontSize: "13px",
    fontWeight: 600,
    marginBottom: "14px",
  },
  title: {
    margin: 0,
    fontSize: "32px",
    fontWeight: 800,
    color: "#1c2148",
    marginBottom: "12px",
  },
  intro: {
    margin: 0,
    fontSize: "15px",
    lineHeight: 1.75,
    color: "#5c6480",
    maxWidth: "700px",
  },
  updated: {
    margin: 0,
    fontSize: "12.5px",
    color: "#9ca3af",
    fontWeight: 600,
    marginTop: "8px",
    marginBottom: "28px",
  },
  card: {
    background: "#fff",
    borderRadius: "20px",
    padding: "26px",
    boxShadow: "0 1px 2px rgba(16,24,40,0.04), 0 8px 28px rgba(16,24,40,0.04)",
    marginBottom: "18px",
  },
  cardTitle: {
    margin: 0,
    fontSize: "17px",
    fontWeight: 800,
    color: "#21284d",
    marginBottom: "14px",
  },
  list: {
    margin: 0,
    paddingLeft: "20px",
    color: "#5c6480",
    fontSize: "14px",
    lineHeight: 1.9,
  },
  contactText: {
    margin: 0,
    fontSize: "14px",
    lineHeight: 1.8,
    color: "#5c6480",
    marginBottom: "14px",
  },
  linkRow: {
    display: "flex",
    gap: "22px",
    flexWrap: "wrap",
  },
  inlineLink: {
    background: "transparent",
    border: "none",
    color: "#6d5efc",
    fontWeight: 700,
    fontSize: "14px",
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    cursor: "pointer",
    padding: 0,
  },
};

export default TermsConditions;
